/**
 * The list `/reports` (ReportsPicker.tsx) reads to build its searchable, categorized
 * list — ADR 0009 §6: "the 88 legacy reports land as list entries with a category, not
 * as navigation." One entry per report actually reachable at `/reports/{slug}`; the
 * component that renders that slug is wired in `App.tsx`'s own `/reports*` branches,
 * not here — this file is data, not routing.
 *
 * FORWARD-COMPATIBLE, ON PURPOSE. Later slices (ef-044-2, ef-044-3, ...) each append one
 * more entry above the `@report-registry-anchor` comment, which stays the LAST line
 * inside the array so every sibling slice's diff is a one-line insertion with no merge
 * conflict against another slice doing the same thing.
 */
export interface ReportRegistryEntry {
  /** The URL segment under `/reports/`, e.g. `"hours"` for `/reports/hours`. */
  slug: string;
  /** The report's accessible name everywhere it's listed — exactly what the picker
   *  renders as the entry's own link text. */
  title: string;
  /** The group heading this entry sits under in the picker. Reused verbatim by every
   *  entry that shares it — same string groups them into one category section. */
  category: string;
  /** One line, shown under the title in the picker only. Never repeated on the report
   *  screen itself — that screen's own PageHeader subtitle is the report's context. */
  description?: string;
}

export const REPORTS: ReportRegistryEntry[] = [
  {
    slug: "statements",
    title: "Statements",
    category: "Financial statements",
    description: "Profit & Loss and Net worth, on a cash or accrual basis.",
  },
  {
    slug: "hours",
    title: "Approved hours",
    category: "Payroll",
    description: "Hours you've approved, per person, over a window you choose.",
  },
  {
    slug: "labour",
    title: "Labour by job code",
    category: "Payroll",
    description: "Approved hours, and an hourly-only estimated cost, rolled up by job code.",
  },
  {
    slug: "payroll-register",
    title: "Payroll register",
    category: "Payroll",
    description: "Gross, each withholding, employer taxes and net pay — per pay run or a date range.",
  },
  {
    slug: "schedule-f",
    title: "Schedule F",
    description: "Cash-basis farm income and expenses, grouped by IRS Schedule F line.",
    category: "Tax",
  },
  {
    slug: "production-sales",
    title: "Production & sales",
    category: "Farm",
    description: "Quantity and amount sold, per kind and unit, over a window you choose.",
  },
  {
    slug: "vendor-purchases",
    title: "Vendor purchases summary",
    category: "Farm",
    description: "Bills, summed per vendor, over a window you choose.",
  },
  {
    slug: "livestock-inventory",
    title: "Livestock inventory",
    category: "Farm",
    description: "Current head counts by herd and status, as of right now.",
  },
  {
    slug: "crop-production",
    title: "Crop production by field",
    category: "Farm",
    description: "Quantity and amount harvested, per field and crop, over a window you choose.",
  },
  {
    slug: "cash-flow",
    title: "Cash flow, actual",
    category: "Financial statements",
    description: "Inflow, outflow and net, by month, from your already-posted activity.",
  },
  {
    slug: "aging",
    title: "AR/AP aging detail",
    category: "Financial statements",
    description: "Every outstanding bill and invoice, bucketed by how overdue it is.",
  },
  // ef-053-0a: five more report slices pre-registered as scaffolding (D1,
  // `ef-053-dispatch.md`) — each still renders its own page's honest "not built" state
  // until its own builder replaces that page. Order matches the wave plan's own table.
  {
    slug: "income-statement",
    title: "Income statement by entity",
    category: "Financial statements",
    description: "Profit & loss for one entity, on a cash or accrual basis.",
  },
  {
    slug: "grain-inventory",
    title: "Grain inventory by bin",
    category: "Farm",
    description: "Current on-hand grain, by bin and crop, across your bins.",
  },
  {
    slug: "milk-production",
    title: "Milk production by month",
    category: "Farm",
    description: "Volume and revenue by month, gal and cwt kept separate.",
  },
  {
    slug: "field-applications",
    title: "Field applications log",
    category: "Farm",
    description: "Chemical, fertilizer and seed applications, per field, over a window you choose.",
  },
  {
    slug: "partner-splits",
    title: "Partner split allocation",
    category: "Farm",
    description: "Who owns what percentage of an entity's proceeds.",
  },
  // ef-054-0a: five more report slices pre-registered as scaffolding (D1,
  // `ef-054-dispatch.md`) — each still renders its own page's honest "not built" state
  // until its own builder replaces that page. Order matches the wave plan's own table.
  {
    slug: "partner-splits-dollar",
    title: "Partner split allocation, dollar",
    category: "Farm",
    description: "Each partner's dollar share of an entity's net income, cash or accrual, as of today.",
  },
  {
    slug: "gl-detail",
    title: "General ledger detail by account",
    category: "Financial statements",
    description: "Every posted line for one account, over a window you choose, in order, with a running balance.",
  },
  {
    slug: "trial-balance",
    title: "Trial balance",
    category: "Financial statements",
    description: "Every account's debit and credit total as of a date — balances to zero.",
  },
  {
    slug: "cash-receipts",
    title: "Cash receipts journal",
    category: "Financial statements",
    description: "Money received into a cash account, over a window you choose.",
  },
  {
    slug: "payroll-tax-liability",
    title: "Payroll tax liability summary",
    category: "Payroll",
    description: "FICA, federal and state withholding owed across finalized pay runs, over a window you choose.",
  },
  {
    // ef-056-5b (ADR 0010 §1) — Books' old cross-account Activity tab, relocated. Route
    // component pre-registered as a stub by the Spec Architect (ef-056-0a); ef-056-5b
    // REPLACES ReportsActivity.tsx's body, not this entry.
    slug: "activity",
    title: "Activity",
    category: "Financial statements",
    description: "Every entry, any account, any source, over a window you choose.",
  },
  // ef-055-0a: two more report slices pre-registered as scaffolding (D1,
  // `ef-055-dispatch.md`) — each still renders its own page's honest "not built" state
  // until its own builder replaces that page.
  {
    slug: "reconciliation",
    title: "Bank reconciliation detail",
    category: "Financial statements",
    description: "Cleared entries and open bank statement lines for one account, as of a date you choose.",
  },
  {
    slug: "crop-planting",
    title: "Crop planting summary",
    category: "Farm",
    description: "What was planted where, and how many acres, for one year.",
  },
  // @report-registry-anchor
];
